/**
 * Client for the new onboarding wizard endpoints (Phase 1).
 *
 * Only one endpoint today: POST /api/onboarding/answers, which takes
 * the full WizardAnswersDto payload collected through the six wizard
 * screens and returns a summary of the priority tasks created, the
 * task IDs the user declined, the LegalZoom doc types they asked to
 * create, and any keyholder seed they entered.
 */
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { createLogger } from '../utils/logger';
import { getAuthToken } from './auth/tokenStorage';
import type {
  WizardAnswers,
  WizardSubmissionResult,
} from '../screens/wizard/WizardContext';

const logger = createLogger('WizardService');

class WizardService {
  /**
   * Submit the completed wizard and receive the priority / decline /
   * LegalZoom CTA summary. Also persists `task_declined_N` AsyncStorage
   * flags for every declined task so the existing checklist screens
   * hide those tasks immediately on the next dashboard view.
   */
  async submitAnswers(answers: WizardAnswers): Promise<WizardSubmissionResult> {
    const token = await getAuthToken();
    if (!token) throw new Error('Not authenticated');

    let response;
    try {
      response = await axios.post(
        `${API_URL}/api/onboarding/answers`,
        answers,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err: any) {
      // Surface the real backend error so the Alert isn't just a generic "could not save"
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message;
      logger.error(`Wizard submit failed (status=${status})`, backendMessage, err?.response?.data);
      throw new Error(backendMessage || 'Network error');
    }

    const result = response.data?.data as WizardSubmissionResult;
    if (!result) {
      logger.error('Wizard response missing data field', response.data);
      throw new Error('Malformed wizard response from server');
    }

    // Mirror declines into AsyncStorage so existing checklist screens
    // that check `task_declined_N` hide the declined tasks.
    await Promise.all(
      result.declinedTaskIds.map(id =>
        AsyncStorage.setItem(`task_declined_${id}`, 'true'),
      ),
    );

    logger.debug(
      `Wizard submitted: ${result.priorityTaskKeys.length} priorities, ` +
      `${result.declinedTaskIds.length} declines, ` +
      `${result.legalZoomDocTypes.length} legalzoom CTAs`,
    );

    return result;
  }
}

export const wizardService = new WizardService();
